import { AUTH_MODE, cookieRequest, ApiError, NetworkError, TimeoutError } from '@/services/api';

// ──────────────────────── 타입 ────────────────────────

export interface AdminUser {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'member';
  isOwner: boolean;
  status: 'active' | 'pending' | 'rejected';
  joinedAt: string;
  approvedAt: string | null;
}

export interface AdminStats {
  totalMembers: number;
  pendingCount: number;
  activeCount: number;
  adminCount: number;
}

export interface FamilySpace {
  id: string;
  name: string;
  inviteCode: string;
  createdAt: string;
  updatedAt: string;
  userCount: number;
  adminCount: number;
  albumCount: number;
  photoCount: number;
}

export interface SessionStats {
  activeSessions: number;
  usersWithSessions: number;
  maxSessionsPerUser: number;
}

export interface SessionCleanupResult extends SessionStats {
  before: number;
  expiredDeleted: number;
  excessDeleted: number;
  message: string;
}

interface ServerAdminUser {
  id: number | string;
  name: string;
  email: string;
  role: 'admin' | 'member';
  isOwner?: boolean;
  status: 'active' | 'pending' | 'rejected';
  createdAt: string;
  approvedAt: string | null;
}

interface UsersResponse {
  users: ServerAdminUser[];
}

interface FamiliesResponse {
  families: Array<Omit<FamilySpace, 'id'> & { id: string | number }>;
  maxFamilies: number;
}

function mapAdminUser(user: ServerAdminUser): AdminUser {
  return {
    id: String(user.id),
    name: user.name,
    email: user.email,
    role: user.role,
    isOwner: Boolean(user.isOwner),
    status: user.status,
    joinedAt: user.createdAt?.slice(0, 10) || '',
    approvedAt: user.approvedAt?.slice(0, 10) || null,
  };
}

function mapFamilySpace(family: FamiliesResponse['families'][number]): FamilySpace {
  return { ...family, id: String(family.id) };
}

const REQUEST_TIMEOUT_MS = 10000;

// ── Mock 모드 상수 ──
const MOCK_USERS_KEY = 'family_album_admin_users';

// ──────────────────────── 시드 데이터 ────────────────────────

const SEED_USERS: AdminUser[] = [
  {
    id: 'admin-001',
    name: '관리자',
    email: 'admin@family.com',
    role: 'admin',
    isOwner: true,
    status: 'active',
    joinedAt: '2025-01-15',
    approvedAt: '2025-01-15',
  },
  {
    id: 'user-001',
    name: '김민준',
    email: 'minjun@family.com',
    role: 'member',
    isOwner: false,
    status: 'active',
    joinedAt: '2025-01-15',
    approvedAt: '2025-01-15',
  },
  {
    id: 'user-002',
    name: '박지은',
    email: 'jieun@family.com',
    role: 'member',
    isOwner: false,
    status: 'active',
    joinedAt: '2025-01-15',
    approvedAt: '2025-01-15',
  },
  {
    id: 'user-003',
    name: '김수아',
    email: 'sua@family.com',
    role: 'member',
    isOwner: false,
    status: 'active',
    joinedAt: '2025-02-01',
    approvedAt: '2025-02-01',
  },
  {
    id: 'user-004',
    name: '김지호',
    email: 'jiho@family.com',
    role: 'member',
    isOwner: false,
    status: 'active',
    joinedAt: '2026-03-15',
    approvedAt: '2026-03-15',
  },
  {
    id: 'user-005',
    name: '김영수',
    email: 'youngsu@family.com',
    role: 'member',
    isOwner: false,
    status: 'active',
    joinedAt: '2025-03-10',
    approvedAt: '2025-03-10',
  },
  {
    id: 'user-006',
    name: '이순자',
    email: 'soonja@family.com',
    role: 'member',
    isOwner: false,
    status: 'active',
    joinedAt: '2025-03-10',
    approvedAt: '2025-03-10',
  },
  {
    id: 'pending-001',
    name: '최예은',
    email: 'yeeun@family.com',
    role: 'member',
    isOwner: false,
    status: 'pending',
    joinedAt: '2026-07-10',
    approvedAt: null,
  },
  {
    id: 'pending-002',
    name: '김도윤',
    email: 'doyun@family.com',
    role: 'member',
    isOwner: false,
    status: 'pending',
    joinedAt: '2026-07-12',
    approvedAt: null,
  },
  {
    id: 'pending-003',
    name: '박하은',
    email: 'haeun@family.com',
    role: 'member',
    isOwner: false,
    status: 'pending',
    joinedAt: '2026-07-13',
    approvedAt: null,
  },
];

// ──────────────────────── 로컬스토리지 헬퍼 ────────────────────────

function readUsers(): AdminUser[] {
  const raw = localStorage.getItem(MOCK_USERS_KEY);
  if (!raw) {
    localStorage.setItem(MOCK_USERS_KEY, JSON.stringify(SEED_USERS));
    return [...SEED_USERS];
  }
  try {
    return JSON.parse(raw) as AdminUser[];
  } catch {
    localStorage.setItem(MOCK_USERS_KEY, JSON.stringify(SEED_USERS));
    return [...SEED_USERS];
  }
}

function writeUsers(users: AdminUser[]): void {
  localStorage.setItem(MOCK_USERS_KEY, JSON.stringify(users));
}

// ──────────────────────── API 모드 요청 ────────────────────────

export class AdminApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'AdminApiError';
    this.status = status;
  }
}

async function adminApiRequest<T>(
  endpoint: string,
  options: { method?: string; body?: string } = {},
): Promise<T> {
  try {
    return await cookieRequest<T>(endpoint, {
      method: options.method || 'GET',
      headers: { 'Content-Type': 'application/json' },
      body: options.body,
    }, REQUEST_TIMEOUT_MS);
  } catch (err) {
    if (err instanceof ApiError) {
      throw new AdminApiError(err.message, err.status);
    }
    if (err instanceof NetworkError) throw err;
    if (err instanceof TimeoutError) throw err;
    throw err;
  }
}

// ──────────────────────── 관리자 API ────────────────────────

export const adminService = {
  /** 전체 사용자 목록 조회 */
  async getUsers(): Promise<AdminUser[]> {
    if (AUTH_MODE === 'mock') {
      await new Promise((r) => setTimeout(r, 300));
      return readUsers();
    }
    const response = await adminApiRequest<UsersResponse>('/api/admin/users');
    return response.users.map(mapAdminUser);
  },

  /** 가입 승인 대기 목록 */
  async getPendingUsers(): Promise<AdminUser[]> {
    if (AUTH_MODE === 'mock') {
      await new Promise((r) => setTimeout(r, 300));
      return readUsers().filter((u) => u.status === 'pending');
    }
    const response = await adminApiRequest<UsersResponse>('/api/admin/users');
    return response.users.map(mapAdminUser).filter((u) => u.status === 'pending');
  },

  /** 승인 */
  async approveUser(userId: string): Promise<void> {
    if (AUTH_MODE === 'mock') {
      await new Promise((r) => setTimeout(r, 400));
      const users = readUsers();
      const idx = users.findIndex((u) => u.id === userId);
      if (idx !== -1) {
        users[idx] = {
          ...users[idx],
          status: 'active',
          approvedAt: new Date().toISOString().split('T')[0],
        };
        writeUsers(users);
      }
      return;
    }
    await adminApiRequest(`/api/admin/users/${userId}/approve`, { method: 'PATCH' });
  },

  /** 삭제 / 거절 */
  async deleteUser(userId: string): Promise<void> {
    if (AUTH_MODE === 'mock') {
      await new Promise((r) => setTimeout(r, 400));
      const users = readUsers();
      writeUsers(users.filter((u) => u.id !== userId));
      return;
    }
    await adminApiRequest(`/api/admin/users/${userId}`, { method: 'DELETE' });
  },

  /** 관리자 권한 부여/해제 */
  async updateUserRole(userId: string, role: 'admin' | 'member'): Promise<AdminUser> {
    if (AUTH_MODE === 'mock') {
      await new Promise((r) => setTimeout(r, 400));
      const users = readUsers();
      const index = users.findIndex((user) => user.id === userId);
      if (index === -1) throw new AdminApiError('사용자를 찾을 수 없습니다.', 404);
      users[index] = { ...users[index], role };
      writeUsers(users);
      return users[index];
    }
    const response = await adminApiRequest<{ user: ServerAdminUser }>(
      `/api/admin/users/${userId}/role`,
      { method: 'PATCH', body: JSON.stringify({ role }) },
    );
    return mapAdminUser(response.user);
  },

  /** 로그인 세션 현황 */
  async getSessionStats(): Promise<SessionStats> {
    if (AUTH_MODE === 'mock') {
      return { activeSessions: 1, usersWithSessions: 1, maxSessionsPerUser: 5 };
    }
    return adminApiRequest<SessionStats>('/api/admin/sessions/stats');
  },

  /** 만료/초과 로그인 세션 정리 */
  async cleanupSessions(): Promise<SessionCleanupResult> {
    if (AUTH_MODE === 'mock') {
      return {
        before: 1,
        expiredDeleted: 0,
        excessDeleted: 0,
        activeSessions: 1,
        usersWithSessions: 1,
        maxSessionsPerUser: 5,
        message: '정리할 세션이 없습니다.',
      };
    }
    return adminApiRequest<SessionCleanupResult>('/api/admin/sessions/cleanup', { method: 'POST' });
  },

  /** 대시보드 통계 */
  async getStats(): Promise<AdminStats> {
    if (AUTH_MODE === 'mock') {
      await new Promise((r) => setTimeout(r, 200));
      const users = readUsers();
      return {
        totalMembers: users.length,
        pendingCount: users.filter((u) => u.status === 'pending').length,
        activeCount: users.filter((u) => u.status === 'active').length,
        adminCount: users.filter((u) => u.role === 'admin').length,
      };
    }
    const response = await adminApiRequest<UsersResponse>('/api/admin/users');
    const users = response.users.map(mapAdminUser);
    return {
      totalMembers: users.length,
      pendingCount: users.filter((u) => u.status === 'pending').length,
      activeCount: users.filter((u) => u.status === 'active').length,
      adminCount: users.filter((u) => u.role === 'admin').length,
    };
  },

  /** 최고 관리자 전용: 가족 공간 목록 */
  async getFamilies(): Promise<{ families: FamilySpace[]; maxFamilies: number }> {
    if (AUTH_MODE === 'mock') {
      return {
        maxFamilies: 3,
        families: [{
          id: '1', name: '우리 가족', inviteCode: '123456',
          createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
          userCount: readUsers().length, adminCount: 1, albumCount: 0, photoCount: 0,
        }],
      };
    }
    const response = await adminApiRequest<FamiliesResponse>('/api/admin/families');
    return { families: response.families.map(mapFamilySpace), maxFamilies: response.maxFamilies };
  },

  /** 최고 관리자 전용: 새 가족 공간 생성 */
  async createFamily(name: string): Promise<FamilySpace> {
    if (AUTH_MODE === 'mock') {
      const existing = await this.getFamilies();
      if (existing.families.length >= existing.maxFamilies) {
        throw new AdminApiError('가족 공간은 최대 3개까지 만들 수 있습니다.', 400);
      }
      return {
        id: String(existing.families.length + 1), name, inviteCode: String(Math.floor(100000 + Math.random() * 900000)),
        createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
        userCount: 0, adminCount: 0, albumCount: 0, photoCount: 0,
      };
    }
    const response = await adminApiRequest<{ family: FamiliesResponse['families'][number] }>('/api/admin/families', {
      method: 'POST', body: JSON.stringify({ name }),
    });
    return mapFamilySpace(response.family);
  },
};
